// Shared carrier-onboarding enums used across backend, frontend, and driver
// portal. Kept here (not in `@repo/db`) so browser bundles can import them
// without pulling the Node-only db package.

export const CARRIER_ONBOARDING_STATUSES = [
  'invited',
  'submitted',
  'under_review',
  'approved',
  'rejected',
  // Terminal, and not a review verdict: the office recalled the invitation
  // before the carrier sent anything in (PET-3831). Kept distinct from
  // `rejected` because rejecting this flow clones a replacement application and
  // mints a fresh invite (`ONB-08`) — the opposite of a recall.
  'closed',
] as const
export type CarrierOnboardingStatus = (typeof CARRIER_ONBOARDING_STATUSES)[number]

// The carrier-writable slugs: every company-scope requirement whose `source` is
// `applicant_upload`, which is what the carrier owns and may therefore replace
// (FLT-21). Not "the four" — `transferOnboardingDocumentsToCarrier` copies every
// company-scope document across with no slug filter, so the workers' comp pair
// lands on this panel too, and both are required on the carrier flow. Gating
// them out hid the only replace control for a required, *expiring* document.
export const CARRIER_DOCUMENT_TYPES = [
  'sunbiz_filing',
  'w9',
  'safer_snapshot',
  'voided_check',
  'workers_comp_certificate',
  'workers_comp_exemption',
] as const

export const CARRIER_DOCUMENT_STATUSES = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'expired',
] as const
export type CarrierDocumentStatus = (typeof CARRIER_DOCUMENT_STATUSES)[number]

// The carrier flow's steps, in order (PET-3167, reordered by its follow-up).
//
// Mirrors the owner-operator flow (`STANDARD_DRIVER_ONBOARDING_STEPS`) as closely
// as the domain allows: an applicant-facing run ending in a pre-submit `review`
// recap, then a terminal office stage. `fleet` is the one carrier addition, and
// `approved` stands where the driver flow has `dispatch`.
//
// Two orderings are load-bearing:
//
//   * `insurance` comes BEFORE `fleet`. The choice decides which documents each
//     pair card asks for, so asked after the cards it was still unanswered while
//     the applicant filled them in and every card rendered with no document tiles
//     at all — the documents only became askable once the Emblem submission had
//     already gone out.
//   * `documents` comes BEFORE the Emblem request, which is no longer a wizard
//     step at all. The office reviews and approves every document first, then
//     sends the request (`CARR-24`). The applicant used to fire it themselves from
//     a `quote` step sitting between `fleet` and `documents`, which asked an agent
//     to price a fleet off paperwork nobody had checked.
//
// Two lists, because `approved` is the office's stage: it belongs in the flow's
// steps so the office progress panel can chip it, but `PATCH /:flow/:token/step`
// must never let an applicant advance themselves into it (`CARR-27`).
//
// The applicant list is the declared one and the full list derives from it.
// Deriving the other way needed a cast, which let the two drift — deleting
// `'approved'` from the full list still compiled while the wizard silently gained
// a step. This way `CarrierWizardStep ⊂ CarrierOnboardingStep` holds by
// construction and both stay real tuples.
export const CARRIER_WIZARD_STEPS = [
  'basic_info',
  'insurance',
  'fleet',
  'documents',
  'review',
] as const
export type CarrierWizardStep = (typeof CARRIER_WIZARD_STEPS)[number]

export const CARRIER_ONBOARDING_STEPS = [...CARRIER_WIZARD_STEPS, 'approved'] as const
export type CarrierOnboardingStep = (typeof CARRIER_ONBOARDING_STEPS)[number]

// ── Fleet pair cards ────────────────────────────────────────────────────────
//
// One card is a truck-and-driver combo: a driver half and a truck half, each
// carrying its own document tiles. The helpers below are the single definition
// of "this card is complete" / "the fleet step is complete", shared by the
// wizard's Next button and the server's step and submit gates. They used to be
// one implementation for a reason (DOC-10): a gate looser than the button it
// backs is how an application gets through with a document missing.

// Fields the applicant fills on a card, as the wizard holds them mid-edit —
// hence `string | null` throughout rather than the validated shapes.
export type CarrierFleetUnitFields = {
  driverFirstName: string | null
  driverLastName: string | null
  driverEmail: string | null
  driverPhone: string | null
  driverDateOfBirth: string | null
  customerTruckNumber: string | null
  vin: string | null
  plateNumber: string | null
  truckType: string | null
}

export const CARRIER_FLEET_UNIT_FIELDS = [
  'driverFirstName',
  'driverLastName',
  'driverEmail',
  'driverPhone',
  'driverDateOfBirth',
  'customerTruckNumber',
  'vin',
  'plateNumber',
  'truckType',
] as const satisfies readonly (keyof CarrierFleetUnitFields)[]
export type CarrierFleetUnitField = (typeof CARRIER_FLEET_UNIT_FIELDS)[number]

// Owed on both paths: the driver's identity is how Allied reaches them and how
// their user account is created at provisioning, neither of which depends on who
// insures the truck.
//
// Everything a card OWES, on both paths — the driver's identity and nothing
// else. This is how Allied reaches them and how their user account is created at
// provisioning, neither of which depends on who insures the truck.
//
// Nothing about the TRUCK is on it. The Allied truck number went in PET-3814
// (Allied assigns it after approval, so a new carrier had nothing to type), then
// vin, plateNumber and truckType in PET-3815: all three of those are printed on
// the Truck Registration, which `DOC-32` already requires per truck on the Emblem
// path and collects on this same card, so requiring them a second time as typing
// blocked an application over data Allied was about to receive anyway
// (Fabrizzio, 2026-09-08 and 2026-09-10 UAT rounds). Make, model and year were
// never owed. That lands the carrier flow on the shape the owner-operator one
// already had — PET-3813 dropped its VIN requirement on the same argument, that
// the VIN's only consumer is the insurance quote and Allied would rather have the
// application than the field.
//
// The whole vehicle spec is still ASKED on the Emblem path, because reading it
// off a form beats opening a PDF: see `isCarrierFleetUnitVehicleSpecAsked`, which
// still varies by path where this list no longer does. REQUIRED is now a strict
// subset of SHOWN, and that is the direction that matters — a field may be shown
// and optional, but never required while hidden, which would be a gate nobody can
// satisfy.
export const CARRIER_FLEET_UNIT_REQUIRED_FIELDS = [
  'driverFirstName',
  'driverLastName',
  'driverEmail',
  'driverPhone',
] as const satisfies readonly CarrierFleetUnitField[]

export function isCarrierFleetUnitVehicleSpecAsked(insurancePath: string | null): boolean {
  return insurancePath === 'needs_insurance'
}

const isBlank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim().length === 0

// `driverDateOfBirth` is conditional rather than listed above: it is only owed on
// the Emblem path, where the medical-card requirement's age atom reads it. The
// caller resolves that (`isFleetDriverDateOfBirthOwed`) and passes the answer,
// which is why no insurance path reaches this function any more — since PET-3815
// the required list itself does not vary by one.
export function missingCarrierFleetUnitFields(
  fields: CarrierFleetUnitFields,
  options: { isDriverDateOfBirthOwed: boolean },
): CarrierFleetUnitField[] {
  const missing = CARRIER_FLEET_UNIT_REQUIRED_FIELDS.filter((field) => isBlank(fields[field]))
  if (options.isDriverDateOfBirthOwed) return [...missing, 'driverDateOfBirth']
  return [...missing]
}

// Whether the whole fleet step is done. Every clause is load-bearing:
//
//   * an unanswered insurance path means we do not yet KNOW what the cards owe.
//     Every per-unit requirement is gated on the path, so with it null the
//     document set resolves to empty and every card would read as satisfied —
//     the step would wave through a fleet with no documents at all, and the
//     applicant would only be asked for them after the Emblem submission had
//     gone out. This is why the choice is made on the fleet step itself;
//   * a null or non-positive declared count means the company step was never
//     answered, so there is nothing to be complete against — the same
//     empty-set trap `areRequiredUnitsSatisfied` guards, where `.every()` over
//     an empty array is vacuously true and would wave a blank step through;
//   * the unit count must MATCH the declared count. Since the count is kept in
//     step with the cards that exist (`syncDeclaredTruckCount`), this no longer
//     holds a carrier to the number they first typed — adding or removing a truck
//     moves both. What it still catches is the gap the seeding leaves: declaring
//     three trucks materializes three cards, and all three have to be filled
//     rather than two filled and one abandoned;
//   * a card is complete only when both its fields and its document tiles are.
export function isCarrierFleetStepComplete(args: {
  insurancePath: string | null
  declaredTruckCount: number | null
  units: readonly { missingFields: readonly string[]; documentsSatisfied: boolean }[]
}): boolean {
  if (args.insurancePath !== 'has_own' && args.insurancePath !== 'needs_insurance') return false
  const declared = args.declaredTruckCount ?? 0
  if (declared <= 0) return false
  if (args.units.length !== declared) return false
  return args.units.every((unit) => unit.missingFields.length === 0 && unit.documentsSatisfied)
}
